import React, { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { useMapData, type MapRegion } from "@/hooks/dashboard/use-map-data";
import { useAfricanAirportsData } from "@/hooks/dashboard/use-african-airports-data";
import { findAirportByCity } from "@/utils/cities/airport-lookup";
import {
  canonicalizeCountryName,
  getFeatureCommonName,
} from "@/utils/countries/africa";

interface BubbleMapWidgetProps {
  data: {
    type: "bubble-map";
    title: string;
    cities: Array<{
      country: string;
      city: string;
      value: number;
      latitude?: string;
      longitude?: string;
    }>;
    meta: any;
    empty: boolean;
  };
  widget: any;
}

export default function BubbleMapWidget({ data, widget }: BubbleMapWidgetProps) {
  const { t } = useTranslation();
  const [hoveredCity, setHoveredCity] = useState<{ city: string; country: string; value: number; x: number; y: number } | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  const mapOptions = widget?.config?.options?.bubbleMap || {};
  const region = mapOptions?.region || "africa";
  const appearance = mapOptions?.appearance || {};
  const showLegend = !!appearance?.showLegend;
  const legendLabel = appearance?.legendLabel || "Value";
  const legendLocation = appearance?.legendLocation || "bottom-center";
  const fillColor = appearance?.fillColor || "#3b82f6";
  const borderColor = appearance?.borderColor || "#ffffff";
  const showCityName = appearance?.showCityName !== false; // default true
  const cityNameColor = appearance?.cityNameColor || "#ffffff";
  const showValueTooltip = appearance?.showValueTooltip !== false; // default true
  const countryFillColor = appearance?.countryFillColor || "#e5e7eb";
  const countryHoverColor = appearance?.countryHoverColor || "#d1d5db";
  const countryBorderColor = appearance?.countryBorderColor || "#d1d5db";
  const showSubtitle = !!appearance?.showSubtitle;
  const subtitle = appearance?.subtitle || "";

  const { data: geoData, loading: geoDataLoading, error: geoDataError } = useMapData(region as MapRegion);
  const { data: airportsData, loading: airportsLoading, error: airportsError } = useAfricanAirportsData();

  // Enrich cities with coordinates from airport data if not already present
  const enrichedCities = useMemo(() => {
    if (!data.cities || data.cities.length === 0) return [];

    // Only use airport data if region is Africa and data is available
    const shouldUseAirportData = region === "africa" && airportsData && !airportsLoading;

    return data.cities.map((city) => {
      // If coordinates already exist, use them
      if (city.latitude && city.longitude) {
        return {
          ...city,
          lat: parseFloat(city.latitude),
          lng: parseFloat(city.longitude),
        };
      }

      // Otherwise, try to find in airport data
      if (shouldUseAirportData) {
        const airport = findAirportByCity(city.city, airportsData);
        if (airport) {
          return {
            ...city,
            lat: parseFloat(airport.latitude),
            lng: parseFloat(airport.longitude),
          };
        }
      }

      // Return city without coordinates (won't be displayed)
      return {
        ...city,
        lat: null,
        lng: null,
      };
    }).filter((city) => city.lat !== null && city.lng !== null);
  }, [data.cities, region, airportsData, airportsLoading]);

  // Calculate bubble size based on value (normalize to reasonable range)
  const maxValue = useMemo(() => {
    if (enrichedCities.length === 0) return 1;
    return Math.max(...enrichedCities.map((c) => c.value), 1);
  }, [enrichedCities]);

  const getBubbleSize = (value: number) => {
    const minSize = 4;
    const maxSize = 20;
    const normalized = maxValue > 0 ? value / maxValue : 0;
    return minSize + (maxSize - minSize) * normalized;
  };

  // Calculate legend values - create 4-5 representative sizes
  const legendValues = useMemo(() => {
    if (enrichedCities.length === 0 || maxValue === 0) return [];
    const minValue = Math.min(...enrichedCities.map((c) => c.value));
    const steps = 4;
    const values: number[] = [];
    for (let i = 0; i <= steps; i++) {
      const value = minValue + (maxValue - minValue) * (i / steps);
      values.push(Math.round(value));
    }
    return values.filter((v, i, arr) => i === 0 || v !== arr[i - 1]); // Remove duplicates
  }, [enrichedCities, maxValue]);

  // Get legend position classes
  const getLegendPositionClasses = () => {
    const positions: Record<string, string> = {
      "top-left": "top-1 left-1 sm:top-2 sm:left-2",
      "top-center": "top-1 left-1/2 -translate-x-1/2 sm:top-2",
      "top-right": "top-1 right-1 sm:top-2 sm:right-2",
      "bottom-left": "bottom-1 left-1 sm:bottom-2 sm:left-2",
      "bottom-center": "bottom-1 left-1/2 -translate-x-1/2 sm:bottom-2",
      "bottom-right": "bottom-1 right-1 sm:bottom-2 sm:right-2",
    };
    return positions[legendLocation] || positions["bottom-center"];
  };

  if (geoDataLoading || (region === "africa" && airportsLoading)) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('forms.loadingMapData')}</div>
      </div>
    );
  }

  if (geoDataError || (region === "africa" && airportsError)) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-red-500">
          {t('forms.errorLoadingMapData', {
            error: geoDataError || airportsError || 'Unknown error'
          })}
        </div>
      </div>
    );
  }

  if (!geoData) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('forms.mapDataNotAvailable')}</div>
      </div>
    );
  }

  if (data.empty || enrichedCities.length === 0) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('processManagement.noDataForSelectedCriteria')}</div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full flex flex-col">
      {showSubtitle && subtitle && (
        <div className="flex-none px-2 py-1 text-xs sm:text-sm text-gray-700 font-medium border-b text-center">
          {subtitle}
        </div>
      )}
      <div
        className="flex-1 relative"
        onMouseMove={(e) => {
          if (hoveredCity) {
            const rect = e.currentTarget.getBoundingClientRect();
            setMousePosition({
              x: e.clientX - rect.left,
              y: e.clientY - rect.top,
            });
          }
        }}
        onMouseLeave={() => {
          setHoveredCity(null);
          setMousePosition(null);
        }}
      >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: region === "world" ? 100 : 600,
          center: region === "world" ? [0, 0] : [20, 0]
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup center={[0, 0]} maxZoom={40} zoom={1}>
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const featureNameRaw = getFeatureCommonName(geo.properties, region);
                const key = canonicalizeCountryName(featureNameRaw);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: countryFillColor,
                        outline: "none",
                        stroke: countryBorderColor,
                        strokeWidth: 0.3,
                      },
                      hover: {
                        fill: countryHoverColor,
                        outline: "none",
                      },
                      pressed: {
                        fill: countryHoverColor,
                        outline: "none",
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Render bubbles */}
          {enrichedCities.map((city, idx) => {
            const size = getBubbleSize(city.value);
            return (
              <Marker key={idx} coordinates={[city.lng, city.lat]}>
                <g
                  onMouseEnter={() => {
                    if (showValueTooltip) {
                      setHoveredCity({
                        city: city.city,
                        country: city.country,
                        value: city.value,
                        x: 0,
                        y: 0,
                      });
                    }
                  }}
                >
                  {/* Bubble circle */}
                  <circle
                    r={size}
                    fill={fillColor}
                    stroke={borderColor}
                    strokeWidth={1.5}
                    style={{ cursor: showValueTooltip ? 'pointer' : 'default' }}
                  />
                  {/* City name label above bubble */}
                  {showCityName && (
                    <text
                      textAnchor="middle"
                      y={-size - 6}
                      style={{
                        fontFamily: "system-ui, sans-serif",
                        fontSize: "12px",
                        fill: cityNameColor,
                        fontWeight: "500",
                        textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        pointerEvents: "none",
                      }}
                    >
                      {city.city}
                    </text>
                  )}
                </g>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Proportional Symbol Legend */}
      {showLegend && legendValues.length > 0 && (
        <div className={`absolute ${getLegendPositionClasses()} bg-white bg-opacity-95 p-2 sm:p-3 md:p-4 rounded-lg shadow-lg z-10 max-w-[calc(100%-1rem)] sm:max-w-none`}>
          <div className="text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 md:mb-3 text-gray-800 truncate">{legendLabel}</div>
          <div className="flex items-end gap-1.5 sm:gap-2 md:gap-3 flex-wrap">
            {legendValues.map((value, idx) => {
              const size = getBubbleSize(value);
              return (
                <div key={idx} className="flex flex-col items-center gap-0.5 sm:gap-1">
                  <div
                    style={{
                      width: `${size * 2}px`,
                      height: `${size * 2}px`,
                      borderRadius: '50%',
                      backgroundColor: fillColor,
                      border: `1.5px solid ${borderColor}`,
                      flexShrink: 0,
                    }}
                  />
                  <span className="text-[10px] sm:text-xs text-gray-700 font-medium whitespace-nowrap">
                    {value.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Value Tooltip */}
      {showValueTooltip && hoveredCity && mousePosition && (
        <div
          className="absolute bg-gray-900 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded shadow-lg z-20 pointer-events-none max-w-[200px] sm:max-w-none"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y - 10}px`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="text-xs sm:text-sm font-semibold truncate">{hoveredCity.country}/{hoveredCity.city}</div>
          <div className="text-[10px] sm:text-xs">{legendLabel}: {hoveredCity.value.toLocaleString()}</div>
        </div>
      )}
      </div>
    </div>
  );
}

